import React from "react";
import * as Mui from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Add,
  Check,
  ChevronLeft,
  ChevronRight,
  Circle,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
} from "@mui/icons-material";

// Poppins font, scoped to just the screens that have opted into this design
// system (Users, RoleAndUserGroup) via a local <ThemeProvider theme={designTheme}>
// wrapped around each screen's own root component. Deliberately NOT set on
// nfb_theme (src/theme.js) — that's the real app-wide root theme, and setting
// it there would silently change every other, not-yet-redesigned module's
// font too. Palette is intentionally left untouched (falls back to MUI's own
// defaults) since nothing in either screen relies on nfb_theme's few custom
// palette keys (Success/Danger/Dark/customColors/Next) via the `color` prop —
// only the stock primary/secondary MUI already defaults to, which nfb_theme
// doesn't override either.
export const designFontFamily =
  '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

export const designTheme = Mui.createTheme({
  typography: { fontFamily: designFontFamily },
});

// Applies ONLY the Poppins font family on top of whichever theme a screen is
// already under, leaving that theme's palette/spacing/etc. untouched. Use this
// (not designTheme) to opt a screen that relies on the app's custom palette
// into the design font — designTheme is a bare MUI theme, so wrapping such a
// screen in it would reset primary/secondary to MUI's stock blue. MUI's
// ThemeProvider accepts a function of the outer theme, so pass this directly:
//   <ThemeProvider theme={withDesignFont}> ...            (inherit + Poppins)
// or call it with an explicit base theme when a screen already sets its own:
//   <ThemeProvider theme={withDesignFont(theme)}> ...     (that base + Poppins)
//
// Must be a single createTheme() call, not createTheme(outerTheme, { typography })
// — outerTheme (e.g. the app's root nfb_theme) is already a fully resolved
// theme, so each variant (body1, h6, button, ...) already carries its own
// baked-in fontFamily from when nfb_theme was first created. A second-argument
// merge only overwrites the top-level typography.fontFamily string; it never
// touches those pre-resolved variants, since MUI merges the extra argument
// onto the already-built theme instead of re-running its typography generator.
// The result: every plain <Typography> (no explicit variant, so it renders as
// body1) silently stayed in nfb_theme's Roboto — Poppins was never applied to
// anything, only to an unused top-level field. Spreading outerTheme into a
// single options object instead makes MUI regenerate every variant fresh from
// this fontFamily, while createPalette/createTheme still reproduce the same
// palette (including nfb_theme's custom keys like Success/Danger) unchanged.
export const withDesignFont = (outerTheme) =>
  Mui.createTheme({ ...outerTheme, typography: { fontFamily: designFontFamily } });

// Sticky `top` for any element that needs to tuck directly under the fixed
// AppBar (Grid height:80, border-box so its borderBottom is included = 80px,
// see Sidebar/index.js -- was 64 + 1px border = 65px before #661 made the
// bar taller). Deliberately 2px less than that 80px: Sidebar/index.js also
// renders a stray <Divider/> + a height:80 spacer *after* the AppBar closes (both in
// normal flow, since the AppBar itself is position:fixed and out of flow),
// so page content's natural top sits 1px lower than the AppBar's true
// bottom edge. On top of that 1px, position:fixed (AppBar) and
// position:sticky (this) can round sub-pixel offsets differently during an
// active scroll — especially at fractional display scaling — which briefly
// exposes a hairline of page background at the seam. Sitting 2px under the
// AppBar's bottom edge is invisible either way (it's inside this element's
// own padding, not real content) and the AppBar's much higher z-index
// (theme.zIndex.appBar, ~1100 vs. our 2-4) safely covers the overlap. Lives
// here (not in a per-screen ui.js) since it's app-shell layout math shared by
// every screen that sticks something under the AppBar, not a design choice
// specific to any one screen.
// Height of the fixed top bar (Sidebar/index.js) and the sidebar's logo
// header beside it. Was 80 (#661); lowered to 64 so the bar takes less room.
export const APP_BAR_HEIGHT = 64;
export const APP_BAR_OFFSET = APP_BAR_HEIGHT - 2;

// Slack-style sidebar (Sidebar/index.js): an always-visible product rail plus
// the selected product's section panel, which the menu button shows/hides.
// Admin.js offsets the page by the same numbers. The panel width is only the
// default: people can drag the panel's edge between the MIN and MAX below
// (Sidebar/index.js keeps the chosen width in the --sidebar-panel-width CSS
// variable and remembers it in this browser).
export const SIDEBAR_RAIL_WIDTH = 72;
export const SIDEBAR_PANEL_WIDTH = 232;
export const SIDEBAR_PANEL_MIN_WIDTH = 184;
export const SIDEBAR_PANEL_MAX_WIDTH = 400;
// CSS length for the panel's current width, usable in calc().
export const SIDEBAR_PANEL_WIDTH_VAR = `var(--sidebar-panel-width, ${SIDEBAR_PANEL_WIDTH}px)`;

// Single source of truth for the teal/slate design language introduced in
// the Administration redesign (Users, then RoleAndUserGroup). Screens opt
// in one at a time as they're redesigned — this isn't wired into every
// module yet, so most of the app still reads its colors from the legacy
// `themes` object in this folder's index.js. Before this file existed, the
// two screens that DID share this look hand-copied the same hex values into
// their own ui.js, and drifted apart silently — e.g. Users' rows-per-page
// focus border was still the legacy dark-green `themes.greenColor` (#008000)
// long after RoleAndUserGroup's identical control had already moved to the
// new teal. Anything here should only ever need to change in one place.
export const designColors = {
  tealDark: "#0F766E",
  teal: "#0D9488",
  tealDarker: "#115E59",
  tealTint: "#E6F8F4",
  slate900: "#111827",
  // slate900: "#000000",
  slate800: "#0F172A",
  slate700: "#334155",
  slate600: "#64748B",
  slate500: "#475569",
  slate400: "#94A3B8",
  slate300: "#CBD5E1",
  slate200: "#E2E8F0",
  slate100: "#EDF2F7",
  slateBg: "#F8FAFC",
  // Neutral page/canvas background behind list views and expanded
  // (full-page) panels. A single token so every screen that opts into this
  // look stays in sync instead of each one hardcoding its own hex that
  // drifts out of step over time. One step deeper than slateBg (#F8FAFC,
  // used for hovers and table headers) so white cards clearly lift off the
  // page; slateBg read as almost white and made screens look flat.
  pageBg: "#F3F5F8",
  borderInput: "#D7E0EA",
  borderInputHover: "#A5B4C7",
  white: "#FFFFFF",
};

export const designRadii = {
  card: "8px",
  control: "8px",
  iconButton: "10px",
  compactButton: "7px",
  select: "9px",
  banner: "8px",
  pill: "999px",
  menu: "12px",
};

// Shared "selected nav row" look, used by BOTH the main app Sidebar
// (Sidebar/index.js, Team Zone scope) and RequestNavRail.js (the Approve /
// My Requests rail). These two are separate component trees for good
// reason (the main sidebar also owns company switching, tooltips, routing,
// badges — RequestNavRail doesn't), but this one visual language — a light
// teal tint + a thin teal border + teal text when selected — was getting
// hand-copied into both and repeatedly drifting apart (active background,
// active text, border-radius, border width/color, font-size at the nested
// level, font-weight all had to be independently re-synced this session).
// Calling this from both instead ends that: change the look once, here, and
// it moves in both places together.
//
// depth: "top" for a rail/menu's primary items (RequestNavRail's items,
// Sidebar's main + submenu levels); "nested" for a further-indented child
// row (RequestNavRail's children, Sidebar's nested level) — nested rows are
// a touch smaller and, when inactive, a shade lighter.
export const navRowContainerSx = ({ active, depth = "top" }) => ({
  borderRadius: designRadii.control,
  // Uniform 1px frame on every side when selected. A thicker borderLeft
  // used to sit here as a separate accent, but mixing a 3px side with a 1px
  // side under the same border-radius makes the rounded corner between them
  // taper unevenly — one width, same color on all sides, avoids that.
  border: active
    ? `1px solid ${designColors.tealDark}`
    : "1px solid transparent",
  // The left accent, brought back as an inset shadow instead of a wider
  // borderLeft — this layers on top of the uniform border above without
  // introducing a second border-width for the rounded corner to reconcile,
  // so it doesn't reintroduce the taper the borderLeft version caused.
  boxShadow: active ? `inset 3px 0 0 ${designColors.tealDark}` : "none",
  backgroundColor: active ? designColors.tealTint : "transparent",
});

export const navRowTextSx = ({ active, depth = "top" }) => ({
  fontFamily: designFontFamily,
  fontSize: depth === "nested" ? 14 : 14.5,
  fontWeight: active ? designWeights.semibold : designWeights.medium,
  color: active
    ? designColors.tealDark
    : depth === "nested"
      ? designColors.slate700
      : designColors.slate900,
});

// Sidebar standard (same for every product), shared by the main Sidebar
// and in-page menus such as Payroll Settings:
// - the current page is the only coloured row: light green band, bold green
//   text, 3px green line on the left;
// - an open section is bold dark text with a down arrow;
// - icons are grey and only on top-level rows (the open section's icon
//   turns dark); child rows have no icons;
// - hover is a very light grey, so it never looks like a selection.
export const NAV_COLORS = {
  icon: "#8A929E",
  hover: "#F2F4F7",
  pageBg: designColors.tealTint,
  pageText: designColors.tealDark,
  guide: designColors.slate300,
};

// depth "top": a product row (section or single page). depth "nested":
// a row inside an open section; it sits against the section's guide line.
export const navPanelRowSx = ({ page = false, depth = "top" }) => ({
  my: 0.25,
  minHeight: depth === "top" ? 40 : 34,
  pr: 1,
  gap: 1.25,
  borderRadius: designRadii.control,
  ...(depth === "top"
    ? { mx: 0.75, pl: 1 }
    : {
        // -1px so the 3px green line covers the 1px guide line.
        ml: "-1px",
        mr: 0.75,
        pl: 2,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      }),
  backgroundColor: page ? NAV_COLORS.pageBg : "transparent",
  boxShadow: page ? `inset 3px 0 0 ${NAV_COLORS.pageText}` : "none",
  transition: "background-color 0.15s ease",
  "&:hover": {
    backgroundColor: page ? NAV_COLORS.pageBg : NAV_COLORS.hover,
  },
});

// page: the current page. strong: an open section (or the one holding
// the current page) -- bold dark, never green.
export const navPanelTextSx = ({ page = false, strong = false, depth = "top" }) => ({
  fontFamily: designFontFamily,
  fontSize: depth === "top" ? 14.5 : 14,
  // Same font, sizes and weights as the sidebar had before the redesign
  // (navRowTextSx): page semibold, open section bold, the rest medium.
  fontWeight: page
    ? designWeights.semibold
    : strong
      ? designWeights.heading
      : designWeights.medium,
  color: page
    ? NAV_COLORS.pageText
    : strong || depth === "top"
      ? designColors.slate900
      : designColors.slate700,
});

export const navPanelIconSx = ({ page = false, strong = false }) => ({
  fontSize: 20,
  flexShrink: 0,
  color: page
    ? NAV_COLORS.pageText
    : strong
      ? designColors.slate900
      : NAV_COLORS.icon,
});

// Down arrow when open, › when closed.
export const navChevronSx = (expanded) => ({
  fontSize: 18,
  flexShrink: 0,
  color: designColors.slate400,
  transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
  transition: "transform 0.2s",
});

// The thin vertical line that child rows of an open section hang from,
// under the section icon's centre.
export const navGuideListSx = (marginLeft) => ({
  ml: marginLeft,
  borderLeft: `1px solid ${NAV_COLORS.guide}`,
});

// Bright, distinct hues for per-row icon tinting in the main app Sidebar
// (Sidebar/index.js) — cycled by each row's position in its own list rather
// than looked up by name, since routes.js (unlike RequestNavRail.js's
// callers, which already hand each item its own `color`) has no per-route
// color of its own to draw on. Centralized here so "how bright" is one
// number to tune (see ICON_TINT_ALPHA below), not 8 colors to re-pick by
// eye — and the same alpha ratio is reused by RequestNavRail.js's own icon
// tint (see its ListItemIcon) so both rails' icons stay one consistent
// family even though one is name-keyed and this one is position-keyed.
export const sidebarIconPalette = [
  "#7C6CF6", // violet
  "#F0954A", // amber
  "#22B07D", // green
  "#3B82F6", // blue
  "#EC6BAE", // pink
  "#14B8A6", // teal
  "#F2635A", // coral
  "#8B5CF6", // purple
];

// 0.55 read as washed-out/shaded; 0.85 was still reported as dull. Raised to
// full strength (the raw palette hex, no fade) — tealDark stays visually
// distinct as the selected state because it's a solid dark teal rather than
// one of these lighter/brighter hues, not because the others were dimmed
// down to make room for it.
export const ICON_TINT_ALPHA = 1;

export const sidebarIconTint = (index) =>
  alpha(sidebarIconPalette[index % sidebarIconPalette.length], ICON_TINT_ALPHA);

// Shared font-weight scale so redesigned screens keep ONE consistent
// typographic hierarchy instead of every element independently reaching for
// 800/900 (which makes a whole screen read as uniformly bold, with no
// heading/body contrast left). Callers pick by intent, not by number:
//   heading  — page title, section/panel headings, and the single key figure
//              a tile is built around (the big count/percent). Genuinely bold.
//   semibold — labels, status chips, buttons, moderate "this matters a little"
//              emphasis.
//   medium   — quiet emphasis: detail-line label/value pairs, table body cells
//              that should read a touch firmer than plain text.
//   regular  — plain reading text: captions, sub-labels, helper lines, most
//              table cells. The default; if in doubt, use this.
// The rule of thumb: on any one card, at most the heading and one figure are
// bold — everything else is regular (or medium where it needs a nudge).
export const designWeights = {
  heading: 700,
  semibold: 600,
  medium: 500,
  regular: 400,
};

// The two "muted secondary line" shapes shared across screens — helperText
// for normal-density panel/detail text (StatCard's detail line, panel
// muted text), captionText for tightly-stacked dense contexts (e.g. a card
// showing several stat lines close together). Kept as separate named tiers
// rather than merged into one size: the 12/13px split already reflects a
// consistent, deliberate density choice made independently across screens,
// not accidental drift.
const helperTextBase = { fontSize: 13, color: designColors.slate600 };
const captionTextBase = { fontSize: 12, color: designColors.slate600 };

// Typography bases shared as-is across screens. Where a screen adds its own
// extra fields (e.g. RoleAndUserGroup's title also sets letterSpacing), spread
// this first and layer the extra fields on top locally rather than editing it
// here, unless every consumer should get that field.
export const designType = {
  pageTitle: {
    fontSize: { xs: 20, md: 26 },
    fontWeight: 700,
    color: designColors.slate900,
    lineHeight: 1.25,
  },
  pageSubtitle: {
    mt: 0.25,
    ...helperTextBase,
  },
  panelTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: designColors.slate800,
  },
  helperText: helperTextBase,
  captionText: captionTextBase,
  // StatCard's title/value text (below) — named so any future stat-style
  // card can reuse the same look without re-typing the numbers.
  cardTitle: { fontSize: 13.5, fontWeight: 600, color: designColors.slate700 },
  // Color is deliberately excluded — every StatCard caller supplies its own
  // category `accent` for the value figure.
  cardValue: { fontSize: 20, fontWeight: 700 },
  // designShell.tableHeadCellBase's typography (below) — named so any other
  // future table-header treatment can reuse the exact same look.
  tableHeaderCell: {
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: "0.01em",
    textTransform: "none",
    color: designColors.slate800,
  },
  // approveTableBodyCell's typography (approveUi.js) — same reasoning.
  tableBodyCell: { fontSize: 14, color: designColors.slate900 },
  // TeamZone > Reports landing page (Reports/index.js) — named as-is from
  // that screen's existing hand-typed values (no size changes), since each
  // was previously duplicated inline instead of read from one place.
  // Generic bold section header within a page body (e.g. "Report
  // Categories") — distinct from panelTitle (larger, for a whole panel).
  sectionHeading: {
    fontSize: 14,
    fontWeight: 700,
    color: designColors.slate900,
  },
  // Report-category card title. Color excluded — each category card colors
  // its own title by its `accent`, the same reasoning as cardValue above.
  categoryCardTitle: { fontSize: 17, fontWeight: 700 },
  categoryCardDescription: {
    fontSize: 13.5,
    lineHeight: 1.65,
    color: designColors.slate700,
  },
  // A single row's link label inside a category card's list — Reports'
  // report links and Approve's Approval Center category rows both use this
  // exact shape.
  reportLinkLabel: { fontSize: 14, color: designColors.slate800 },
  // The "N Reports" footer count on a category card. Color excluded — same
  // per-category `accent` reasoning as categoryCardTitle.
  categoryCountLabel: { fontSize: 14, fontWeight: 700 },
  // The "Can't find the report you're looking for?" callout title/body.
  calloutTitle: {
    fontSize: 14.5,
    fontWeight: 700,
    color: designColors.slate900,
  },
  calloutText: { fontSize: 13.5, color: designColors.slate600 },
  // Approve/index.js's Approval Center dashboard cards (Leave & Time Off,
  // Attendance & Workday, etc.) — the card title. Color excluded — each
  // card colors its own title by that category's `accent`.
  approvalCardTitle: { fontSize: 15, fontWeight: 700 },
};

// Single shared width for the Cancel/Create/Save/Update button pairs that
// close out a form or panel footer across Administration. Before this
// existed, each screen picked its own pair (AddCompany: unset Cancel next to
// a 160 Create; UserDetailPanel/CompanyDetailPanel: 120 Cancel next to a 160
// Update; RoleAndUserGroup: 128 Cancel next to a 180 Create) — the two
// buttons in the same row never actually matched. panelCloseButton and the
// PrimaryButton/SecondaryButton components (DesignSystemButtons.js) both
// default to this so any screen that doesn't pass its own minWidth gets a
// matching pair automatically; RoleAndUserGroup's ui.js also points its
// primaryButton/secondaryButton tokens here for the same reason.
export const FORM_ACTION_BUTTON_WIDTH = 160;

// Single shared width for the page-header "+ Add ..." primary action button
// (Users' "Create User", Companies' "Add Company", RoleAndUserGroup's "Add
// Role"/"Add User Group" toggle). Before this existed each header picked its
// own value — Users at 160, Companies and RoleAndUserGroup at 180 — so the
// same kind of button read as a different size depending on which list
// screen you were on. 180 was already the majority value (2 of 3 screens,
// and the one that has to fit the longest label, "Add User Group", without
// visibly resizing when RoleAndUserGroup's tab toggles to "Add Role").
export const HEADER_ACTION_BUTTON_WIDTH = 180;

// Card/table/control shells that were byte-identical between Users and
// RoleAndUserGroup before this file existed.
export const designShell = {
  sectionCard: {
    backgroundColor: designColors.white,
    border: `1px solid ${designColors.slate200}`,
    borderRadius: designRadii.card,
    boxShadow: "0 8px 30px rgba(15, 23, 42, 0.04)",
  },
  // Row/card "..." action menu (Edit, Deactivate, etc.) for the teal/slate
  // design system. RoleAndUserGroup had already hand-rolled this exact look
  // per-screen; pulling it out here so Users/Companies can match it instead
  // of falling back to MUI's plain default Menu (sharp corners, no shadow).
  // Kept intentionally faint — tight blur radius, low alpha — just enough
  // to lift it off the page without the dark-halo look earlier versions had.
  actionMenuPaper: {
    borderRadius: "16px",
    border: `1px solid ${designColors.slate200}`,
    minWidth: 180,
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
  },
  // fontFamily set explicitly (not just inherited via ThemeProvider) since
  // this text isn't wrapped in Mui.Typography — matches fieldValue's weight
  // (adminListScreenUi.js) rather than MenuItem's plain MUI default.
  actionMenuItem: {
    fontFamily: designFontFamily,
    fontSize: 14,
    fontWeight: 500,
    color: designColors.slate800,
    py: 1,
  },
  tableCard: {
    overflow: "hidden",
  },
  pageSurface: {
    backgroundColor: designColors.pageBg,
    // minHeight: "100%" is a no-op wherever this renders under Sidebar's
    // <main> (see Sidebar/index.js) — that wrapper has no defined height,
    // so a percentage resolves against nothing and the page only grows to
    // fit its own content, leaving the real viewport uncovered below short
    // tables. Pin to the viewport instead, minus the fixed 80px AppBar
    // spacer, so the background always reaches the bottom of the screen.
    minHeight: `calc(100vh - ${APP_BAR_HEIGHT}px)`,
  },
  // alignItems/mb deliberately excluded — Users centers at `sm` with mb:2,
  // RoleAndUserGroup centers at `md` with mb:3. Each screen's own headerRow
  // token spreads this and adds those two fields itself.
  headerRowBase: {
    display: "flex",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  },
  // Base column-header cell look, no sticky positioning — Users' own
  // tableHeadCell spreads this and adds position:sticky/top/zIndex on top,
  // since RoleAndUserGroup's tables don't stick.
  tableHeadCellBase: {
    ...designType.tableHeaderCell,
    borderBottom: `1px solid ${designColors.slate200}`,
    backgroundColor: designColors.slate100,
  },
  tableStickyHead: {
    position: "relative",
    zIndex: 3,
    backgroundColor: designColors.slate100,
    "& th": {
      backgroundColor: designColors.slate100,
    },
  },
  tableStickyHeadRow: {
    backgroundColor: designColors.slate100,
    "& th": {
      backgroundColor: designColors.slate100,
    },
  },
  tableRowHover: {
    "&:hover td": {
      backgroundColor: designColors.slate100,
    },
  },
  neutralSelectedRow: {
    backgroundColor: designColors.slateBg,
    borderLeft: `3px solid ${designColors.slate300}`,
    "& td:first-of-type": { pl: "13px" },
    "& td": {
      backgroundColor: designColors.slateBg,
    },
    "&:hover td": {
      backgroundColor: designColors.slateBg,
    },
  },
  accentSelectedRow: {
    backgroundColor: designColors.tealTint,
    borderLeft: `3px solid ${designColors.tealDark}`,
    "& td:first-of-type": { pl: "13px" },
    "& td": {
      backgroundColor: designColors.tealTint,
    },
    "&:hover td": {
      backgroundColor: designColors.tealTint,
    },
  },
  paginationIconButton: {
    width: 30,
    height: 30,
    borderRadius: designRadii.compactButton,
    border: `1px solid ${designColors.slate200}`,
    color: designColors.slate500,
    "&:hover": {
      backgroundColor: designColors.slateBg,
      borderColor: designColors.slate300,
    },
    "&.Mui-disabled": {
      color: designColors.slate300,
      borderColor: designColors.slate100,
    },
  },
  rowsPerPageSelect: {
    minHeight: 32,
    borderRadius: designRadii.select,
    fontSize: 12,
    fontWeight: 600,
    color: designColors.slate800,
    backgroundColor: designColors.white,
    "& .MuiSelect-select": {
      py: 0.5,
      pl: 1,
      pr: 3.5,
      display: "flex",
      alignItems: "center",
    },
    "& fieldset": { borderColor: designColors.borderInput },
    "&:hover fieldset": { borderColor: designColors.borderInputHover },
    "&.Mui-focused fieldset": { borderColor: designColors.teal },
  },
  // Filter-bar dropdown (financial year / pay period / status pickers that sit
  // above a list table). Same teal-on-focus border language as
  // rowsPerPageSelect, but taller and roomier so it reads as a primary filter
  // control rather than the compact footer page-size picker. Pulled up here so
  // every list screen that grows a filter row (My Zone Payslips first) shares
  // one look instead of each one hardcoding its own select sizing.
  filterSelect: {
    minWidth: 150,
    borderRadius: designRadii.select,
    fontSize: 13,
    fontWeight: 500,
    color: designColors.slate700,
    backgroundColor: designColors.white,
    "& .MuiSelect-select": {
      py: 1,
      display: "flex",
      alignItems: "center",
    },
    "& fieldset": { borderColor: designColors.borderInput },
    "&:hover fieldset": { borderColor: designColors.borderInputHover },
    "&.Mui-focused fieldset": { borderColor: designColors.teal },
  },
  // Search box that shares the filter row with filterSelect above — same
  // radius/border language so the two controls line up as one bar.
  filterSearchField: {
    minWidth: 220,
    backgroundColor: designColors.white,
    "& .MuiOutlinedInput-root": {
      borderRadius: designRadii.select,
      fontSize: 13,
      "& fieldset": { borderColor: designColors.borderInput },
      "&:hover fieldset": { borderColor: designColors.borderInputHover },
      "&.Mui-focused fieldset": { borderColor: designColors.teal },
    },
  },
  // Gradient/shadow/hover only — minWidth is deliberately left to each
  // screen (Users: 160, RoleAndUserGroup: 180) since it's a per-layout call,
  // not part of the shared look.
  primaryButtonBase: {
    minHeight: 46,
    px: 2.5,
    borderRadius: designRadii.control,
    textTransform: "none",
    fontSize: 14,
    fontWeight: 600,
    color: designColors.white,
    background: `linear-gradient(135deg, ${designColors.tealDark} 0%, ${designColors.teal} 100%)`,
    boxShadow: "0 14px 28px rgba(13, 148, 136, 0.22)",
    "&:hover": {
      background: `linear-gradient(135deg, ${designColors.tealDarker} 0%, ${designColors.tealDark} 100%)`,
    },
    "&.Mui-disabled": {
      background: designColors.slate200,
      color: designColors.slate400,
      boxShadow: "none",
    },
  },
  panelActionButton: {
    minHeight: 46,
    borderRadius: designRadii.control,
    textTransform: "none",
    fontSize: 14,
    fontWeight: 600,
    // Every screen that uses this token (Reject/Cancel buttons across
    // TeamZone Approve's detail panels, RequestCancelButton) also sets its
    // own branded color/borderColor/backgroundColor after spreading this
    // in — without an explicit disabled override here, that custom color
    // just stays fully visible even once the button is actually disabled
    // (checkAccess/hasApproverAccess false), since a plain sx color
    // doesn't reliably lose to MUI's own .Mui-disabled styling. Matches
    // primaryButtonBase's own disabled treatment above, adapted for an
    // outlined rather than filled button.
    "&.Mui-disabled": {
      color: designColors.slate400,
      borderColor: designColors.slate200,
      backgroundColor: designColors.white,
    },
  },
  panelNeutralButton: {
    minHeight: 46,
    borderRadius: designRadii.control,
    textTransform: "none",
    fontSize: 14,
    fontWeight: 600,
    color: designColors.slate700,
    borderColor: designColors.slate300,
    "&:hover": {
      borderColor: designColors.slate400,
      backgroundColor: designColors.slateBg,
      color: designColors.slate800,
    },
  },
  panelCloseButton: {
    minWidth: FORM_ACTION_BUTTON_WIDTH,
    minHeight: 42,
    borderRadius: designRadii.control,
    textTransform: "none",
    fontSize: 14,
    fontWeight: 500,
    color: designColors.slate700,
    borderColor: designColors.slate200,
    backgroundColor: designColors.white,
    "&:hover": {
      backgroundColor: designColors.slateBg,
      borderColor: designColors.slate300,
    },
  },
  panelDangerButton: {
    minHeight: 46,
    borderRadius: designRadii.control,
    textTransform: "none",
    fontSize: 14,
    fontWeight: 600,
    // Same fix as panelActionButton above, for the grid-layout Reject
    // buttons (CompOff/Break/OnDuty/Permission/OffDayDuty/OverTime/
    // AttendanceCorrection/ShiftCorrection detail panels) that use this
    // token instead — their own branded red stayed visible even while
    // disabled without this.
    "&.Mui-disabled": {
      color: designColors.slate400,
      borderColor: designColors.slate200,
      backgroundColor: designColors.white,
    },
  },
  panelHeaderIconButton: {
    border: "1px solid",
    borderColor: designColors.slate200,
    borderRadius: designRadii.iconButton,
    backgroundColor: designColors.white,
    color: designColors.slate600,
    "&:hover": {
      backgroundColor: designColors.slateBg,
    },
  },
  panelSoftIconButton: {
    borderRadius: designRadii.iconButton,
    backgroundColor: designColors.slateBg,
    color: designColors.slate600,
    "&:hover": {
      backgroundColor: designColors.slate100,
    },
  },
  // Compact outlined CTA used on dashboard-style cards (Team Zone Home's
  // "View all" / "Go to Approval Centre" buttons) — a smaller footprint than
  // primaryButtonBase/panelActionButton, which are sized for form footers.
  // Centralized here (rather than left as a per-screen local const) since My
  // Zone Home needs the identical control once it adopts this design system.
  compactOutlineButton: {
    minHeight: 36,
    borderRadius: designRadii.control,
    textTransform: "none",
    fontSize: 13.5,
    fontWeight: 600,
    color: designColors.tealDark,
    borderColor: designColors.slate200,
    backgroundColor: designColors.white,
    "&:hover": {
      backgroundColor: designColors.slateBg,
      borderColor: designColors.slate300,
    },
  },
  panelTabs: {
    mt: 0.25,
    minHeight: 40,
    "& .MuiTab-root": {
      textTransform: "none",
      fontSize: 13,
      minHeight: 40,
      color: designColors.slate600,
    },
    "& .Mui-selected": {
      color: `${designColors.tealDark} !important`,
      fontWeight: 700,
    },
    "& .MuiTabs-indicator": {
      backgroundColor: designColors.tealDark,
    },
  },
};

// Canonical "stat / filter" tile shared by every screen on this design
// system that shows a row of count cards — Administration's Users/Companies
// totals, Team Zone Home's workforce summary, the Approval Center's
// Pending/Approved/Rejected/Cancelled row, and Leave's own status filter
// cards. Before this existed each screen hand-rolled its own Mui.Paper for
// this — different icon size, border, shadow, active-state treatment — and
// drifted apart the same way the page background/button tokens above did
// before those existed. Deliberately borderless (shadow-only, no
// sectionCard border/active-color ring) — plain and quiet is the intended
// look here; `isActive` is conveyed by the scale bump and checkmark badge
// alone. Passing `onClick` opts a card into "filter" behavior — cursor,
// scale-on-active, and a taller tile (click targets want more breathing
// room) — so any future filterable stat row should pass `onClick` to get
// this sizing automatically, the same way Users/Companies/Leave do now.
// Omit it for a purely informational tile (Team Zone Home, Approval
// Center's summary row): those stay at the original compact height.
// `icon` takes a component reference (rendered here as `<Icon />`), not a
// pre-built element — matches every existing caller.
// variant="tinted" washes the whole card body in its own light `tint`
// color instead of the default plain white -- opt-in only, so the many
// existing plain-white callers of this shared card are unaffected. The
// icon chip inverts to white in that mode so it still stands out against
// the now-colored card instead of disappearing into a same-color square.
export const StatCard = ({
  icon: Icon,
  title,
  value,
  detail,
  accent,
  tint,
  onClick,
  isActive = false,
  variant,
  iconRadius,
  iconSize = 42,
  // Opt-in: draw a light border (in the card's own accent) on the selected
  // card only. Unselected cards reserve a transparent border so nothing
  // shifts when the selection changes.
  activeBorder = false,
}) => {
  const tinted = variant === "tinted";
  return (
  <Mui.Box
    onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    aria-label={onClick ? `${title} (${value})` : undefined}
    aria-pressed={onClick ? isActive : undefined}
    onKeyDown={onClick ? event => {
      if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        onClick(event);
      }
    } : undefined}
    sx={{
      ...designShell.sectionCard,
      border: activeBorder ? "1.5px solid" : "none",
      ...(activeBorder
        ? { borderColor: onClick && isActive ? alpha(accent, 0.45) : "transparent" }
        : {}),
      ...(tinted ? { backgroundColor: tint } : {}),
      display: "flex",
      alignItems: "center",
      // On phones the text drops under the icon when the card is too narrow
      // for both side by side (2-up grids), instead of breaking mid-word.
      flexWrap: { xs: "wrap", sm: "nowrap" },
      gap: { xs: 1, sm: 1.35 },
      position: "relative",
      minWidth: 0,
      ...(onClick
        ? {
            px: { xs: 1.25, sm: 1.75 },
            py: { xs: 1.25, sm: 1.75 },
            minHeight: { xs: 84, sm: 100 },
            cursor: "pointer",
            transform: isActive ? "scale(1.02)" : "scale(1)",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
          }
        : {
            px: { xs: 1.25, sm: 1.75 },
            py: 1.25,
            minHeight: 78,
          }),
    }}
  >
    <Mui.Box
      sx={{
        width: { xs: Math.min(iconSize, 34), sm: iconSize },
        height: { xs: Math.min(iconSize, 34), sm: iconSize },
        borderRadius: iconRadius || 1.8,
        backgroundColor: tinted ? designColors.white : tint,
        color: accent,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon fontSize="medium" />
    </Mui.Box>
    <Mui.Box
      sx={{
        minWidth: 0,
        flex: { xs: "1 1 96px", sm: "0 1 auto" },
        pr: onClick && isActive ? { xs: 1.5, sm: 0 } : 0,
      }}
    >
      <Mui.Typography sx={{ ...designType.cardTitle, overflowWrap: "anywhere" }}>{title}</Mui.Typography>
      <Mui.Typography sx={{ ...designType.cardValue, color: accent, mt: 0.35 }}>
        {value}
      </Mui.Typography>
      {detail ? (
        <Mui.Typography
          sx={{
            ...designType.helperText,
            fontSize: { xs: 12, sm: 13 },
            overflowWrap: "anywhere",
            mt: 0.2,
          }}
        >
          {detail}
        </Mui.Typography>
      ) : null}
    </Mui.Box>
    {onClick && isActive ? (
      <Mui.Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          width: 18,
          height: 18,
          borderRadius: "50%",
          backgroundColor: accent,
          color: designColors.white,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Check sx={{ fontSize: 12 }} />
      </Mui.Box>
    ) : null}
  </Mui.Box>
  );
};

// Shared page-header composition: title/subtitle on the left (with room for
// a screen-specific block underneath, e.g. RoleAndUserGroup's Roles/User
// Groups tab toggle, via `extraContent`) and one primary action button on
// the right. Was duplicated as two separate Box/Typography/Button trees
// (Administration's usersHeaderRow, RoleAndUserGroup's listHeaderActions)
// before this existed. `sx` is each screen's own headerRow token (see
// headerRowBase above) so mb/alignItems stay per-screen.
// showPrimaryAction defaults to true so every existing caller keeps
// rendering the button unchanged -- pass false to hide it outright rather
// than showing a button that just toasts "Access denied" on click, the
// same "hide it, don't show-then-block" treatment TeamZone Approve's
// NewRequestButton already uses for its own create action.
export const pageHeaderRow = ({
  title,
  subtitle,
  primaryLabel,
  onPrimaryClick,
  primaryButtonSx,
  extraContent,
  sx,
  showPrimaryAction = true,
}) => (
  <Mui.Box sx={sx}>
    <Mui.Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Mui.Typography sx={designType.pageTitle}>{title}</Mui.Typography>
      <Mui.Typography sx={designType.pageSubtitle}>{subtitle}</Mui.Typography>
      {extraContent}
    </Mui.Box>
    {showPrimaryAction && (
      <Mui.Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <Mui.Button
          startIcon={<Add />}
          sx={{ ...designShell.primaryButtonBase, ...primaryButtonSx }}
          onClick={onPrimaryClick}
        >
          {primaryLabel}
        </Mui.Button>
      </Mui.Stack>
    )}
  </Mui.Box>
);

// "Active" is teal, everything else (Inactive, or any other status label a
// screen passes in) is a neutral slate — not red. Confirmed deliberately:
// red would read as a warning/danger state, and "inactive" is a normal,
// unremarkable state a user can toggle back at any time, not a problem.
export const statusChipStyle = (active) => ({
  height: 30,
  borderRadius: designRadii.pill,
  px: 1,
  backgroundColor: active ? designColors.tealTint : "#F1F5F9",
  color: active ? designColors.tealDark : designColors.slate500,
  border: `1px solid ${active ? "#B7E4DB" : "#DCE3EB"}`,
  "& .MuiChip-label": {
    px: 1,
    fontSize: 13,
    fontWeight: 600,
  },
});

// Additional named tones beyond the original Active/teal vs everything-else/
// slate binary above — added for System Configuration's Trial/Licensed/
// Internal/Expired access states, which need more than two colors (Expired
// is a real problem state and *should* read as red, unlike a deliberate
// Inactive toggle). Any label not listed here falls through to the exact
// original statusChipStyle(label === "Active") behavior, so every existing
// caller (Users, Companies, RoleAndUserGroup) renders unchanged.
const namedStatusTones = {
  Trial: { text: "#B54708", bg: "#FFF6E8", border: "#FEDF89" },
  Licensed: { text: designColors.tealDark, bg: designColors.tealTint, border: "#B7E4DB" },
  Internal: { text: designColors.slate600, bg: designColors.slate100, border: designColors.slate200 },
  Expired: { text: "#B42318", bg: "#FEF3F2", border: "#FECDCA" },
};

const namedStatusChipStyle = (tone) => ({
  height: 30,
  borderRadius: designRadii.pill,
  px: 1,
  backgroundColor: tone.bg,
  color: tone.text,
  border: `1px solid ${tone.border}`,
  "& .MuiChip-label": {
    px: 1,
    fontSize: 13,
    fontWeight: 600,
  },
});

export const statusChip = (label = "Active") => {
  const namedTone = namedStatusTones[label];
  return (
    <Mui.Chip
      size="small"
      icon={
        <Circle
          sx={{
            fontSize: "10px !important",
            color: `${namedTone ? namedTone.text : (label === "Active" ? designColors.tealDark : designColors.slate600)} !important`,
          }}
        />
      }
      label={label}
      sx={namedTone ? namedStatusChipStyle(namedTone) : statusChipStyle(label === "Active")}
    />
  );
};

// Standard width for a form-style side panel in this design system (first
// introduced for Users' Create User panel).
export const PANEL_DRAWER_WIDTH = 620;

// A panel Drawer is a MUI "temporary" variant — position:fixed in a portal,
// so its opaque paper draws on top of everything, including the app's own
// persistent sidebar. That ruled out ever widening this same Drawer to
// "expand" a panel (see git history on this constant): even capped short of
// the sidebar's width, it's still an overlay with a backdrop sitting above
// the rest of the app. "Expand" is instead a full swap — the panel's owner
// stops rendering this Drawer at all and renders the panel's content inline
// in the normal page flow instead (see CreateUser's `expanded` prop), which
// leaves the sidebar and top bar exactly where they always are since
// nothing is overlaying them anymore. This token only ever describes the
// panel's *collapsed* Drawer width.
//
// The paper AND the backdrop both start at APP_BAR_OFFSET (not 0) and lose
// that much height, so neither one dims or paints over the fixed AppBar the
// way the rest of "on top of everything" above still applies to the page
// content. Same "tuck directly under the AppBar" treatment as the expanded
// full-page overlay above (top: APP_BAR_OFFSET there too) — here applied to
// the Drawer's own paper/backdrop instead of a hand-built portal box, so it
// covers every collapsed-Drawer panel built on this token in one place.
//
// APP_BAR_OFFSET is 2px short of the AppBar's real height on purpose (see
// its own comment) — fine for the expanded overlay, which sits at a lower
// z-index than the AppBar so that sliver is safely painted over. A Drawer
// is the opposite: MUI defaults it to theme.zIndex.drawer (1200), *above*
// the AppBar's 1100, so without a z-index override this panel's opaque
// paper would win that same 2px sliver instead and erase the AppBar's
// bottom border for the width of the panel. Pinning the root below the
// AppBar here makes the AppBar win that sliver too, same outcome as the
// overlay, via the opposite fix.
//
// 8% tint of slate800 — the one shared "a panel is open" dimming color for
// every side panel in the app. Baked into panelDrawerSx below (rather than
// left for each caller to set its own ModalProps.BackdropProps) since every
// Drawer-based panel — GlobalSidePanel plus the older hand-rolled Drawers
// that predate it (Approve's Apply.js screens, PunchDevice, ...) — already
// builds its sx from this one function. Before this existed, several of
// those hand-rolled Drawers had each separately hand-typed this same rgba
// value while GlobalSidePanel set "transparent" instead, so opening one
// panel dimmed the page and opening another didn't. One color, set once,
// used by every consumer of panelDrawerSx, keeps that from drifting again.
export const PANEL_BACKDROP_COLOR = "rgba(15, 23, 42, 0.08)";

export const panelDrawerSx = (width = PANEL_DRAWER_WIDTH) => ({
  zIndex: (theme) => ({
    xs: theme.zIndex.drawer + 2,
    sm: theme.zIndex.appBar - 1,
  }),
  "& .MuiBackdrop-root": {
    top: APP_BAR_OFFSET,
    height: `calc(100% - ${APP_BAR_OFFSET}px)`,
    backgroundColor: PANEL_BACKDROP_COLOR,
  },
  "& .MuiDrawer-paper": {
    top: APP_BAR_OFFSET,
    height: `calc(100% - ${APP_BAR_OFFSET}px)`,
    width: { xs: "100vw", sm: width },
    maxWidth: { xs: "100vw", sm: "92vw" },
    backgroundColor: designColors.white,
    // MUI's default Paper elevation shadow blurs outward past the panel's
    // own edges — up over the AppBar (which top/height above stop short
    // of covering, but a shadow isn't clipped by that boundary) and left
    // onto the page behind it. A flat border reads as the same "this is a
    // panel" separation without that spill.
    boxShadow: "none",
    borderLeft: `1px solid ${designColors.slate200}`,
  },
});

// Standard rows-per-page choices across Administration (Companies,
// Departments, Master/Screens, Settings, RoleAndUserGroup, ...). Users used
// to be the one outlier at [10, 25, 50, 100] — now matches everything else.
export const rowsPerPageOptions = [25, 50, 75, 100];

// Was defined once per screen (Administration/ui.js, RoleAndUserGroup/ui.js)
// with identical JSX. `options` still has no module-level default here —
// callers pass rowsPerPageOptions above explicitly, so a caller that ever
// needs a genuinely different set of choices isn't fighting a hidden default.
export const rowsPerPageControl = ({ value, onChange, options }) => (
  <Mui.Stack direction="row" spacing={0.75} alignItems="center">
    <Mui.Typography
      sx={{ fontSize: 12, color: designColors.slate600, whiteSpace: "nowrap" }}
    >
      Rows per page
    </Mui.Typography>
    <Mui.Select
      size="small"
      value={value}
      onChange={onChange}
      sx={designShell.rowsPerPageSelect}
      MenuProps={{
        PaperProps: {
          sx: {
            borderRadius: designRadii.menu,
            border: `1px solid ${designColors.slate200}`,
          },
        },
      }}
    >
      {options.map((option) => (
        <Mui.MenuItem key={option} value={option}>
          {option}
        </Mui.MenuItem>
      ))}
    </Mui.Select>
  </Mui.Stack>
);

export const paginationFooter = ({
  page,
  rowsPerPage,
  total,
  countIsEstimate = false,
  itemLabel = "items",
  summaryExtra,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions: options,
  // Narrow drawer/panel contexts (e.g. LeaveBalanceDetailPanel's History
  // tab) don't have room for the "Showing X-Y of Z" label and the page
  // controls side by side, so the default wrap drops the controls onto a
  // second line. compact keeps everything on one row instead — the caller
  // is expected to place this inside the same horizontally-scrollable,
  // fixed-minWidth container as the table above it, so both scroll
  // together as one region rather than each getting its own scrollbar.
  compact = false,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const start = total === 0 ? 0 : page * rowsPerPage + 1;
  const end = Math.min(total, page * rowsPerPage + rowsPerPage);
  const isFirstPage = page <= 0;
  const isLastPage = page >= totalPages - 1;

  return (
    <Mui.Stack
      direction="row"
      flexWrap={compact ? "nowrap" : "wrap"}
      rowGap={0.75}
      columnGap={1.5}
      justifyContent="space-between"
      alignItems="center"
    >
      <Mui.Stack direction="row" alignItems="center" useFlexGap flexWrap="wrap" sx={{ gap: 1.5, minWidth: 0 }}>
      <Mui.Typography
        sx={{
          fontSize: 14,
          color: designColors.slate600,
          // Long item labels ("attendance correction requests") must wrap on
          // phones instead of running past the screen edge.
          whiteSpace: compact ? "nowrap" : { xs: "normal", sm: "nowrap" },
          minWidth: 0,
          overflowWrap: "anywhere",
        }}
      >
        Showing{" "}
        <Mui.Box
          component="span"
          sx={{ fontWeight: 700, color: designColors.tealDark }}
        >
          {start}–{end}
        </Mui.Box>{" "}
        of{" "}
        <Mui.Box
          component="span"
          sx={{ fontWeight: 700, color: designColors.tealDark }}
        >
          {countIsEstimate ? `about ${total}` : total}
        </Mui.Box>{" "}
        {itemLabel}
      </Mui.Typography>
      {summaryExtra}
      </Mui.Stack>
      <Mui.Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        sx={{ gap: 0.75, flexWrap: compact ? "nowrap" : "wrap", minWidth: 0 }}
      >
        {rowsPerPageControl({
          value: rowsPerPage,
          onChange: onRowsPerPageChange,
          options,
        })}
        <Mui.Stack
          direction="row"
          alignItems="center"
          sx={{ gap: 0.5, flexWrap: compact ? "nowrap" : "wrap", justifyContent: "flex-end" }}
        >
          <Mui.IconButton
            size="small"
            disabled={isFirstPage}
            onClick={() => onPageChange(0)}
            sx={designShell.paginationIconButton}
          >
            <KeyboardDoubleArrowLeft fontSize="small" />
          </Mui.IconButton>
          <Mui.IconButton
            size="small"
            disabled={isFirstPage}
            onClick={() => onPageChange(page - 1)}
            sx={designShell.paginationIconButton}
          >
            <ChevronLeft fontSize="small" />
          </Mui.IconButton>
          <Mui.Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: designColors.slate700,
              px: 0.25,
              whiteSpace: "nowrap",
            }}
          >
            Page {page + 1} of {countIsEstimate ? "about " : ""}{totalPages}
          </Mui.Typography>
          <Mui.IconButton
            size="small"
            disabled={isLastPage}
            onClick={() => onPageChange(page + 1)}
            sx={designShell.paginationIconButton}
          >
            <ChevronRight fontSize="small" />
          </Mui.IconButton>
          <Mui.IconButton
            size="small"
            disabled={isLastPage}
            onClick={() => onPageChange(totalPages - 1)}
            sx={designShell.paginationIconButton}
          >
            <KeyboardDoubleArrowRight fontSize="small" />
          </Mui.IconButton>
        </Mui.Stack>
      </Mui.Stack>
    </Mui.Stack>
  );
};

// Lightweight Prev/Next-only pager for nested drill-down tables — e.g. a
// single expanded date-wise row's employee list, which can run into the
// thousands for a busy date even though the outer table is paginated. A full
// paginationFooter (rows-per-page selector, jump-to-first/last) is too heavy
// for a small embedded section, so page size is fixed by the caller rather
// than user-selectable. Renders nothing when everything already fits on one
// page, so it stays invisible for the common case.
export const compactRowPager = ({
  page,
  pageSize,
  total,
  itemLabel = "items",
  onPageChange,
}) => {
  if (total <= pageSize) {
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : page * pageSize + 1;
  const end = Math.min(total, page * pageSize + pageSize);
  const isFirstPage = page <= 0;
  const isLastPage = page >= totalPages - 1;

  return (
    <Mui.Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ px: 1, py: 0.75 }}
    >
      <Mui.Typography sx={{ fontSize: 12.5, color: designColors.slate600 }}>
        Showing{" "}
        <Mui.Box
          component="span"
          sx={{ fontWeight: 700, color: designColors.tealDark }}
        >
          {start}–{end}
        </Mui.Box>{" "}
        of{" "}
        <Mui.Box
          component="span"
          sx={{ fontWeight: 700, color: designColors.tealDark }}
        >
          {total}
        </Mui.Box>{" "}
        {itemLabel}
      </Mui.Typography>
      <Mui.Stack direction="row" spacing={0.5} alignItems="center">
        <Mui.IconButton
          size="small"
          disabled={isFirstPage}
          onClick={() => onPageChange(page - 1)}
          sx={designShell.paginationIconButton}
        >
          <ChevronLeft fontSize="small" />
        </Mui.IconButton>
        <Mui.Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: designColors.slate700,
            px: 0.25,
            whiteSpace: "nowrap",
          }}
        >
          Page {page + 1} of {totalPages}
        </Mui.Typography>
        <Mui.IconButton
          size="small"
          disabled={isLastPage}
          onClick={() => onPageChange(page + 1)}
          sx={designShell.paginationIconButton}
        >
          <ChevronRight fontSize="small" />
        </Mui.IconButton>
      </Mui.Stack>
    </Mui.Stack>
  );
};
